import { formatActivityData } from '@openeducation/activity-player-formatter';
import { Icon } from '@openeducation/oe-rn-components';
import { ChatBotLessonInfo } from '@openeducation/oe-rn-shared-types';
import {
  FinishLessonAction,
  LessonInfo,
  LessonPlayer,
  PlayerViewerType,
  useLessonContext,
} from '@openeducation/react-native-lesson-player';
import React, { useEffect, useState } from 'react';
import { Text, TouchableOpacity, View } from 'react-native';

import styles from './lesson.style';
import { LessonEvent } from '../../enums/lesson-event';
import { useConfig } from '../../hooks/useConfig';
import { useLessonInfo } from '../../hooks/useLessonInfo';
import { useListenToMessage } from '../../hooks/useListenToMessage';
import { getUrlParams } from '../../utils/get-url-params';
import { isIframe } from '../../utils/is-iframe';
import { postMessage } from '../../utils/post-message';
import { ErrorScreen } from '../errors/error';
import { Header } from '../header/header';
import { Loading } from '../loading/loading';

export function Lesson() {
  const { toggleIndexVisibility, listenToEvent } = useLessonContext();
  const params = getUrlParams();
  const [data, setData] = useState<{
    rawData: Record<string, unknown>;
    formattedData: Record<string, unknown>;
  } | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const { config, isLoaded } = useConfig();
  const [lessonInfo, setLessonInfo] = useState<LessonInfo>();
  const displayHeader = () => !isIframe() || params.topBar === true;

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    if (!config) {
      setError(new Error('There was a problem retrieving the config.'));
      return;
    }

    const fetchOptions: RequestInit = {
      headers: {
        'cache-control': 'no-cache',
      },
    };

    const language = params.language || 'es';

    const getAssetsBaseUrl = (url: string, regex = /(.*)\/\w+\.json.*/): string => {
      const match = url?.match(regex);
      if (!match) {
        return '';
      }

      return match[1];
    };

    if (params.activityUrl) {
      const url = params.activityUrl;
      const assetsBaseUrl = getAssetsBaseUrl(url);
      fetch(url, fetchOptions)
        .then((result) => result.json())
        .then((activity) => {
          const formattedActivity = formatActivityData(activity, language, assetsBaseUrl);
          const data = { name: activity.name, slides: [formattedActivity] };
          setData({ formattedData: data, rawData: activity });
        })
        .catch((e) => {
          setError(
            new Error(
              `It looks like the data from ${params.activityUrl} is not valid.\n${e.message}`
            )
          );
          console.error(e);
        });
    } else if (params.indexUrl) {
      const url = params.indexUrl;
      const assetsBaseUrl = getAssetsBaseUrl(url, /(.*)\/\d+\/lessons\/\d+\/\w+\.json.*/);
      fetch(url, fetchOptions)
        .then((result) => result.json())
        .then((data) => {
          const slides = data?.slides?.map((slide: any) =>
            formatActivityData(
              slide,
              language,
              `${assetsBaseUrl}/${slide.projectId}/${slide.elementId}`
            )
          );
          setData({ formattedData: { ...data, slides }, rawData: data });
        })
        .catch((e) => {
          setError(
            new Error(`It looks like the data from ${params.indexUrl} is not valid.\n${e.message}`)
          );
          console.error(e);
        });
    } else if (params.lessonId) {
      const [programId, lessonId] = params.lessonId.toString().split('/');
      if (!programId || !lessonId) {
        setError(
          new Error('It looks like the lesson is not valid. It must be missing program or lesson')
        );
        return;
      }

      const assetsBaseUrl = `${config?.bucketUrl}`;
      const url = `${assetsBaseUrl}/${programId}/lessons/${lessonId}/index.json`;
      fetch(url, fetchOptions)
        .then((result) => result.json())
        .then((data) => {
          const slides = data?.slides?.map((slide: any) =>
            formatActivityData(
              slide,
              language,
              `${assetsBaseUrl}/${slide.projectId}/${slide.elementId}`
            )
          );
          setData({ formattedData: { ...data, slides }, rawData: data });
        })
        .catch((e) => {
          setError(
            new Error(
              `It looks like the data from the lesson with program ${programId} and lesson ${lessonId} is not valid`
            )
          );
          console.error(e);
        });
    } else {
      setError(new Error('It looks like the lesson id is not set'));
    }
  }, [isLoaded, config]);

  useLessonInfo((lessonInfo) => setLessonInfo(lessonInfo));

  useListenToMessage((event) => {
    if (!event) {
      return;
    }

    const { data } = event;
    if (data === LessonEvent.TOGGLE_INDEX) {
      toggleIndexVisibility();
    }
  });

  const finishLesson = (action?: FinishLessonAction) => {
    postMessage({ type: LessonEvent.FINISH_LESSON, action });
  };

  const redirectToReferralPage = () => {
    postMessage({ type: LessonEvent.REFERRAL_BUTTON_CLICKED });
  };

  useEffect(() => {
    const handleLessonEvent = (event: any) => {
      postMessage(event);
    };

    const subscription = listenToEvent(handleLessonEvent);

    return () => {
      subscription.remove();
    };
  }, [listenToEvent]);

  const onChatBotClick = React.useCallback(
    (lessonInfo: ChatBotLessonInfo) => {
      postMessage({
        type: LessonEvent.OPEN_CHAT_BOT_LESSON,
        value: {
          chatTypes: ['lesson'],
          lessonInfo: {
            courseId: params.courseId,
            ...lessonInfo,
          },
        },
      });
    },
    [params.courseId]
  );

  if (error) {
    return <ErrorScreen message={error.message} />;
  }

  if (!data) {
    return <Loading />;
  }

  return (
    <>
      {displayHeader() && (
        <Header color="#444444">
          <Text style={styles.title}>{(data?.formattedData?.name as string) || 'Activity'}</Text>
          <TouchableOpacity onPress={toggleIndexVisibility}>
            <View>
              <Icon name="list" size={22} color="white" />
            </View>
          </TouchableOpacity>
        </Header>
      )}
      <LessonPlayer
        id={data.formattedData.id}
        viewerType={params.viewerType}
        slides={data?.formattedData.slides}
        rawData={data?.rawData}
        bookmark={params.bookmark}
        complete={params.complete}
        finishLesson={finishLesson}
        redirectToReferralPage={redirectToReferralPage}
        lessonInfo={lessonInfo}
        navHeader={
          params.viewerType === PlayerViewerType.LESSON && {
            breadcrumbTitle: lessonInfo?.breadcrumb,
          }
        }
        onChatBotClick={onChatBotClick}
        copilotEnabled={params.copilotEnabled}
      />
    </>
  );
}
